(function () {
  'use strict';

  /* ─── State ──────────────────────────────────────────────── */
  var slides = [];
  var current = 0;
  var assetBase = '';
  var isTransitioning = false;
  var slideCache = {}; // keyed by slide index → resolved card data

  var autoTimer    = null;   // setInterval handle
  var pauseTimer   = null;   // setTimeout handle (20s pause after manual nav)
  var AUTO_INTERVAL  = 7500; // ms between auto-advances
  var PAUSE_DURATION = 7500; // ms pause after manual nav before resuming

  /* ─── DOM refs ───────────────────────────────────────────── */
  var root, fanStage, cards, navEl, prevBtn, nextBtn, navLabel, slideBg;

  /* ─── Entry point ────────────────────────────────────────── */
  function init() {
    root = document.querySelector('.hs-root');
    if (!root) return;

    var slidesUrl = root.dataset.slidesUrl;
    if (!slidesUrl) {
      console.warn('[HeroShowcase] Missing data-slides-url attribute');
      return;
    }

    assetBase = deriveAssetBase(slidesUrl);
    reorderMobileHeading();

    fetch(slidesUrl)
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        if (!data.slides || !data.slides.length) {
          console.warn('[HeroShowcase] hero-slides.json has no slides');
          return;
        }
        setup(data.slides);
      })
      .catch(function (err) {
        console.warn('[HeroShowcase] Failed to load slides config:', err);
      });
  }

  /* ─── Setup after config is loaded ──────────────────────── */
  function setup(slidesData) {
    slides = slidesData;
    current = Math.floor(Math.random() * slides.length);

    fanStage = root.querySelector('.hs-fan-stage');
    cards    = Array.from(root.querySelectorAll('.hs-card'));
    navEl    = root.querySelector('.hs-nav');
    prevBtn  = root.querySelector('.hs-nav-btn--prev');
    nextBtn  = root.querySelector('.hs-nav-btn--next');
    navLabel = root.querySelector('.hs-nav-label');
    slideBg  = root.querySelector('.hs-slide-bg');

    if (slides.length <= 1) {
      navEl.classList.add('hs-nav--hidden');
    }

    fetchSlideData(current).then(function (resolvedCards) {
      renderSlide(current, resolvedCards);
      triggerRiseAnimation();
      if (slides.length > 1) startAutoRotate();
    });

    if (prevBtn) prevBtn.addEventListener('click', function () { pauseAutoRotate(); navigateTo(current - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { pauseAutoRotate(); navigateTo(current + 1); });

    if (fanStage) {
      fanStage.addEventListener('mouseenter', function () { stopAutoRotate(); });
      fanStage.addEventListener('mouseleave', function () { if (!pauseTimer) startAutoRotate(); });
    }

    /* Title hover — JS driven to bypass pointer-events propagation quirks */
    cards.forEach(function (card) {
      var titleEl = card.querySelector('.hs-card-title');
      if (!titleEl) return;
      card.addEventListener('mouseenter', function () { titleEl.style.opacity = '1'; });
      card.addEventListener('mouseleave', function () { titleEl.style.opacity = ''; });
    });
  }

  /* ─── Navigate to a slide index ─────────────────────────── */
  function navigateTo(index) {
    if (isTransitioning) return;

    index = ((index % slides.length) + slides.length) % slides.length;
    if (index === current) return;

    isTransitioning = true;

    fadeOut().then(function () {
      fetchSlideData(index).then(function (resolvedCards) {
        renderSlide(index, resolvedCards);
        fadeIn();
        triggerRiseAnimation();
      });
    });
  }

  /* ─── Fetch and resolve card data for a slide ───────────── */
  /* Each card can have:
     - "product": "handle"           → fetch from /products/{handle}.js, use featured image + title
     - "product": "handle",
       "variant": "Siberian Tiger"   → same fetch, but pick that variant's image + name
     - "image": "filename"           → use asset URL (static fallback)
     "title" on any card overrides the displayed label.
     Returns a Promise<Array<{ imageUrl, title }>> */
  function fetchSlideData(index) {
    if (slideCache[index]) {
      return Promise.resolve(slideCache[index]);
    }

    var slide = slides[index];
    var promises = slide.cards.map(function (card) {
      if (card.product) {
        return fetch('/products/' + card.product + '.js')
          .then(function (r) {
            if (!r.ok) throw new Error('HTTP ' + r.status);
            return r.json();
          })
          .then(function (p) {
            var img, title;

            if (card.variant) {
              var v = findVariant(p.variants, card.variant);
              if (v) {
                img   = (v.featured_image && v.featured_image.src) || p.featured_image || '';
                title = v.title;
              } else {
                console.warn('[HeroShowcase] Variant "' + card.variant + '" not found in "' + card.product + '" — using product image');
                img   = p.featured_image || '';
                title = p.title || card.product;
              }
            } else {
              img   = p.featured_image || '';
              title = p.title || card.product;
            }

            /* Shopify sometimes returns protocol-relative URLs (//cdn...) */
            if (img && img.indexOf('//') === 0) img = 'https:' + img;
            return { imageUrl: img, title: card.title || title, productUrl: '/products/' + p.handle };
          })
          .catch(function (err) {
            console.warn('[HeroShowcase] Could not load product "' + card.product + '":', err);
            return { imageUrl: '', title: card.variant || card.product };
          });
      } else if (card.image) {
        return Promise.resolve({
          imageUrl: assetBase + card.image,
          title: card.title || ''
        });
      } else {
        return Promise.resolve({ imageUrl: '', title: '' });
      }
    });

    return Promise.all(promises).then(function (resolvedCards) {
      slideCache[index] = resolvedCards;
      return resolvedCards;
    });
  }

  /* ─── Render a slide using pre-resolved card data ────────── */
  function renderSlide(index, resolvedCards) {
    current = index;
    var slide = slides[index];

    /* Slide label — clickable if collectionUrl is set */
    if (navLabel) {
      if (slide.collectionUrl) {
        navLabel.innerHTML = '<a href="' + slide.collectionUrl + '" class="hs-nav-label-link">' + (slide.label || '') + '</a>';
      } else {
        navLabel.textContent = slide.label || '';
      }
    }

    /* Per-slide background image */
    if (slideBg) {
      if (slide.bgImage) {
        slideBg.classList.remove('is-loaded');
        slideBg.onload = function () { slideBg.classList.add('is-loaded'); };
        slideBg.src = slide.bgImage;
      } else {
        slideBg.classList.remove('is-loaded');
        slideBg.src = '';
      }
    }

    /* Cards */
    cards.forEach(function (card, i) {
      var data = resolvedCards[i];

      if (!data) {
        card.classList.add('hs-card--hidden');
        return;
      }

      card.classList.remove('hs-card--hidden');

      var img   = card.querySelector('.hs-card-img');
      var title = card.querySelector('.hs-card-title');

      if (img) {
        img.src = data.imageUrl;
        img.alt = data.title;
        img.style.visibility = '';
        img.onerror = function () { img.style.visibility = 'hidden'; };
      }

      if (title) {
        title.textContent = data.title;
      }

      /* Make card clickable if it has a product URL */
      if (data.productUrl) {
        card.style.cursor = 'pointer';
        card.onclick = function () { window.location.href = data.productUrl; };
      } else {
        card.style.cursor = 'default';
        card.onclick = null;
      }
    });
  }

  /* ─── CSS rise-in animation ──────────────────────────────── */
  function triggerRiseAnimation() {
    cards.forEach(function (card) {
      card.classList.remove('is-animating');
    });

    cards.forEach(function (card) {
      void card.offsetWidth; // force reflow to restart animation
    });

    cards.forEach(function (card) {
      card.classList.add('is-animating');
    });

    setTimeout(function () {
      cards.forEach(function (card) {
        card.classList.remove('is-animating');
      });
      isTransitioning = false;
    }, 800);
  }

  /* ─── Fade helpers ───────────────────────────────────────── */
  function fadeOut() {
    return new Promise(function (resolve) {
      if (!fanStage) { resolve(); return; }
      fanStage.classList.add('is-fading');
      setTimeout(resolve, 200);
    });
  }

  function fadeIn() {
    if (!fanStage) return;
    fanStage.classList.remove('is-fading');
  }

  /* ─── Auto-rotate ────────────────────────────────────────── */
  function startAutoRotate() {
    clearInterval(autoTimer);
    clearTimeout(pauseTimer);
    pauseTimer = null;
    autoTimer = setInterval(function () {
      navigateTo(current + 1);
    }, AUTO_INTERVAL);
  }

  function stopAutoRotate() {
    clearInterval(autoTimer);
    autoTimer = null;
  }

  function pauseAutoRotate() {
    stopAutoRotate();
    clearTimeout(pauseTimer);
    pauseTimer = setTimeout(function () {
      startAutoRotate();
    }, PAUSE_DURATION);
  }

  /* ─── Utility ────────────────────────────────────────────── */

  /* Find a variant by title. Tries exact match first, then partial.
     Case-insensitive. Returns null if nothing matches. */
  function findVariant(variants, query) {
    if (!variants || !variants.length) return null;
    var q = query.toLowerCase();
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].title.toLowerCase() === q) return variants[i];
    }
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].title.toLowerCase().indexOf(q) !== -1) return variants[i];
    }
    return null;
  }

  function deriveAssetBase(url) {
    var clean = url.split('?')[0];
    return clean.substring(0, clean.lastIndexOf('/') + 1);
  }

  /* ─── Mobile heading reorder ────────────────────────────── */
  /* On mobile: physically move the heading block (the .hs-left child that
     contains an h1/h2) out of .hs-left and into .hs-content before .hs-right.
     Desktop is unaffected — JS only runs when ≤900px. */
  function reorderMobileHeading() {
    if (!window.matchMedia('(max-width: 900px)').matches) return;
    var hsContent = root.querySelector('.hs-content');
    var hsLeft    = root.querySelector('.hs-left');
    var hsRight   = root.querySelector('.hs-right');
    if (!hsContent || !hsLeft || !hsRight) return;

    for (var i = 0; i < hsLeft.children.length; i++) {
      if (hsLeft.children[i].querySelector('h1, h2')) {
        var headingBlock = hsLeft.children[i];
        headingBlock.classList.add('hs-mobile-heading');
        hsContent.insertBefore(headingBlock, hsRight);
        break;
      }
    }
  }

  /* ─── Initialise on DOM ready ────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
